/**
 *
 * @module hlsJsPlayerAdapter
 * @desc Standard HLS.js PlayerAdapter
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.com/video-dev/hls.js/blob/master/doc/API.md#runtime-events
 * @see https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Media_events
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class HlsJsPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 * @throws {InvalidArgumentException}
 *
 */
export default class HlsJsPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @throws {InvalidArgumentException}
     * @note The HlsJsPlayerAdapter requires nativePlayer.loadSource
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'HlsJsPlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        Check.assert.function(Util.getSafe(() => nativePlayer.loadSource), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Object}
         * @desc The {HTMLMediaElement} tied to the nativePlayer instance.
         *
         */
        this.mediaElement = nativePlayer.media;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @throws {InvalidStateException} Unable to set playlistUri on NativePlayer
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'HlsJsPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            },
            get setPlaylistUri() {
                return `${this.method} unable to set playlistUri on NativePlayer`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve, reject) => {

            this.playlistUri = playlistUri;

            try {

                nativePlayer.loadSource(playlistUri);

                return resolve();

            } catch (ex) {

                const reasons = [new ErrorReason('', `${errorMessage.setPlaylistUri} - ${ex}`)];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            }

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead or bitrate data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'HlsJsPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, mediaElement, unavailableMetric } = this;
        const { currentLevel } = nativePlayer;

        if (Check.not.assigned(currentLevel.bitrate)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.assigned(mediaElement.currentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = Math.floor(currentLevel.bitrate) || unavailableMetric;
        const currentPlayhead = Math.floor(mediaElement.currentTime) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'HlsJsPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { boundHandlers, mediaElement } = this;
        const { onPlay, onPause, onError, onPlayedToCompletion } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(mediaElement.play)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onPause = onPause.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                mediaElement.onplay = boundHandlers.onPlay;
                mediaElement.onpause = boundHandlers.onPause;
                mediaElement.onerror = boundHandlers.onError;
                mediaElement.onended = boundHandlers.onPlayedToCompletion;

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'HlsJsPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { mediaElement } = this;

        return new Promise((resolve) => {

            if (Check.function(mediaElement.play) && Check.equal(this.listener, listener)) {

                this.listener = null;

                mediaElement.onplay = null;
                mediaElement.onpause = null;
                mediaElement.onerror = null;
                mediaElement.onended = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @returns {Promise<Void>}
     *
     */
    setDrmProvider(drmProvider) {

        const errorMessage = {
            method: 'HlsJsPlayerAdapter.setDrmProvider(drmProvider)',
            get drmProvider() {
                return `${this.method} needs SDK.Drm.DrmProvider instance`;
            }
        };

        instanceStrict(drmProvider, DrmProvider, errorMessage.drmProvider, InvalidArgumentException);

        this.drmProvider = drmProvider;

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;
        this.mediaElement = null;

        return Promise.resolve();

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/hlsJsPlayerAdapter.js